import { useState } from 'react';
import { C, DEGRADE_ACTION } from '../../theme';
import { api } from '../../api/routes';
import { messageDErreur } from '../../api/useDonnees';
import { ChoixCercle, libelleDuCercle } from './ChoixCercle';
import type { Cercle, ChampsCercle, MembreFoyer } from '../../api/types';

/**
 * L'insigne de cercle d'une carte : « Privé », « Partagé avec Marianne »… Rien quand la
 * chose est au foyer (le defaut ne merite pas de bruit) — sauf pour le CREATEUR, qui
 * garde un petit acces « Pour : le foyer » afin de pouvoir changer d'avis apres coup.
 * Les elements d'avant les cercles (createur inconnu) et les evenements Google n'ont
 * pas d'insigne du tout.
 */
export function InsigneCercle({
  genre,
  id,
  cercle,
  membres,
  utilisateurId,
  editable,
  signaler,
  rafraichir,
}: {
  genre: 'evenement' | 'tache' | 'rappel';
  id: string;
  cercle: ChampsCercle;
  membres: MembreFoyer[];
  utilisateurId: string;
  /** false la ou un bouton ne peut pas vivre (carte deja cliquable) : insigne statique. */
  editable: boolean;
  signaler: (message: string) => void;
  rafraichir: () => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [choix, setChoix] = useState<Cercle>({ visibility: 'HOUSEHOLD', sharedWithUserIds: [] });
  const [envoi, setEnvoi] = useState(false);

  if (cercle.visibility === undefined) return null;
  const estCreateur = cercle.createdByUserId === utilisateurId;
  const libelle = libelleDuCercle(cercle, membres);

  // Pas createur : un simple etat, seulement quand il dit quelque chose.
  if (!estCreateur || !editable) {
    return libelle === null ? null : (
      <span style={{ fontSize: 11.5, fontWeight: 700, color: C.tertiaire }}>{libelle}</span>
    );
  }

  const ouvrir = () => {
    setChoix({
      visibility: cercle.visibility ?? 'HOUSEHOLD',
      sharedWithUserIds: cercle.sharedWithUserIds ?? [],
    });
    setOuvert(true);
  };

  const enregistrer = async () => {
    if (envoi) return;
    if (choix.visibility === 'CHOSEN' && choix.sharedWithUserIds.length === 0) {
      signaler('Choisis au moins une personne');
      return;
    }
    setEnvoi(true);
    try {
      await api.changerCercle(genre, id, choix);
      setOuvert(false);
      signaler('C’est noté : le cercle a changé');
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  };

  if (!ouvert) {
    return (
      <button
        type="button"
        onClick={ouvrir}
        aria-label={`Changer qui voit : ${libelle ?? 'tout le foyer'}`}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          fontSize: 11.5,
          fontWeight: 800,
          fontFamily: 'inherit',
          color: libelle === null ? C.tertiaire : C.violetTexte,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        {libelle ?? 'Pour : le foyer'}
      </button>
    );
  }

  return (
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.violetBordure}`,
        borderRadius: 12,
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <ChoixCercle
        membres={membres}
        utilisateurId={utilisateurId}
        valeur={choix}
        onChange={setChoix}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => setOuvert(false)}
          style={{
            border: `1px solid ${C.bordure}`,
            background: C.carte,
            color: C.secondaire,
            borderRadius: 10,
            padding: '7px 12px',
            fontSize: 13,
            fontWeight: 700,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
        <button
          type="button"
          disabled={envoi}
          onClick={() => void enregistrer()}
          style={{
            border: 'none',
            background: envoi ? C.inactif : DEGRADE_ACTION,
            color: '#FFFFFF',
            borderRadius: 10,
            padding: '7px 14px',
            fontSize: 13,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: envoi ? 'default' : 'pointer',
          }}
        >
          {envoi ? '…' : 'OK'}
        </button>
      </div>
    </div>
  );
}
